import { useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { preferencesAPI } from '../api/preferences.api'
import { Preferences } from '../types/preferences.types'

// Default preferences structure
const getDefaultPreferences = (): Preferences => ({
  informations: {
    domicileAddresses: [
      { id: 1, name: 'Domicile 1', street: '', city: '', postalCode: '' }
    ],
    phoneNumber: '',
    email: '',
    emergencyContact: ''
  },
  availability: {
    monday: {
      startingDomicile: 1,
      slots: [
        { start: '09:00', end: '12:00', enabled: true },
        { start: '14:00', end: '17:00', enabled: true },
        { start: '', end: '', enabled: false }
      ]
    },
    tuesday: {
      startingDomicile: 1,
      slots: [
        { start: '09:00', end: '12:00', enabled: true },
        { start: '14:00', end: '17:00', enabled: true },
        { start: '', end: '', enabled: false }
      ]
    },
    wednesday: {
      startingDomicile: 1,
      slots: [
        { start: '09:00', end: '12:00', enabled: true },
        { start: '14:00', end: '17:00', enabled: true },
        { start: '', end: '', enabled: false }
      ]
    },
    thursday: {
      startingDomicile: 1,
      slots: [
        { start: '09:00', end: '12:00', enabled: true },
        { start: '14:00', end: '17:00', enabled: true },
        { start: '', end: '', enabled: false }
      ]
    },
    friday: {
      startingDomicile: 1,
      slots: [
        { start: '09:00', end: '12:00', enabled: true },
        { start: '14:00', end: '17:00', enabled: true },
        { start: '', end: '', enabled: false }
      ]
    },
    saturday: {
      startingDomicile: 1,
      slots: [
        { start: '', end: '', enabled: false },
        { start: '', end: '', enabled: false },
        { start: '', end: '', enabled: false }
      ]
    },
    sunday: {
      startingDomicile: 1,
      slots: [
        { start: '', end: '', enabled: false },
        { start: '', end: '', enabled: false },
        { start: '', end: '', enabled: false }
      ]
    }
  },
  expertiseTypes: {
    expertisesImmobilieres: false,
    certificationPEB: false,
    permisLocation: false
  },
  selectedProvinces: {},
  selectedCommunes: {},
  maxExpertisesPerDay: 3,
  preferredLanguage: 'fr',
  notificationPreferences: {
    email: true,
    sms: true,
    push: false
  }
})

export const usePreferences = () => {
  const queryClient = useQueryClient()
  const originalPreferencesRef = useRef<Preferences | null>(null)
  
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['preferences'],
    queryFn: async () => {
      try {
        const result = await preferencesAPI.getPreferences()
        return result
      } catch (error) {
        // Return default preferences if fetch fails
        console.warn('Using default preferences due to error:', error)
        return getDefaultPreferences()
      }
    },
    staleTime: 0, // Always fetch fresh data when the component mounts
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    refetchOnMount: true, // Always refetch when component mounts
    refetchOnWindowFocus: false, // Don't refetch on window focus
    retry: 1
  })
  
  const saveMutation = useMutation({
    mutationFn: preferencesAPI.savePreferences,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['preferences'] })
      alert('Préférences sauvegardées avec succès')
    },
    onError: (error) => {
      console.error('Error saving preferences:', error)
      alert('Erreur lors de la sauvegarde des préférences')
    }
  })
  
  useEffect(() => {
    if (data && !originalPreferencesRef.current) {
      originalPreferencesRef.current = JSON.parse(JSON.stringify(data))
    }
  }, [data])
  
  return {
    preferences: data || getDefaultPreferences(),
    isLoading,
    error,
    savePreferences: saveMutation.mutate,
    savePreferencesAsync: saveMutation.mutateAsync,
    isSaving: saveMutation.isPending,
    originalPreferences: originalPreferencesRef.current,
    refetch
  }
}